import React, { useState } from "react";
import { Link } from "react-router-dom";
import * as hrmsService from "../../../services/hrmsService";
import { HR_PERMISSIONS, hasHrPermission } from "../../../constants/hrPermissions";
import { FormDrawer } from "../hrForm";
import { EmptyState, LoadState, PageHeader, PrimaryButton, Tabs } from "../hrComponents";
import { useHr } from "../HrGate";
import { cardStyle, formatDate, formatRange, pageStyle, tdStyle, thStyle, useLoad } from "../hrUi";
import PayrollInputsTab from "./PayrollInputsTab";
import PayrollReportsTab from "./PayrollReportsTab";
import PayrollSetupTab from "./PayrollSetupTab";
import { RUN_TYPE_LABEL, defaultPayMonth, runStatusBadge, runStatusLabel } from "./payrollHelpers";

const TYPE_OPTIONS = [
  { label: "Monthly payroll", value: "REGULAR" },
  { label: "Off-cycle (bonus, arrears, one-time items)", value: "OFF_CYCLE" },
  { label: "Full and final settlement", value: "FNF" },
];

function NewRunDrawer({ show, onHide, onDone }) {
  const fields = (v) => [
    { key: "kind", label: "What to pay", type: "select", options: TYPE_OPTIONS },
    ...(v.kind === "FNF"
      ? [
          { key: "employee_id", label: "Employee who is leaving", type: "employee", required: true, hint: "They need a separation with a last working day" },
          { key: "leave_encashment_days", label: "Unused leave to pay (days)", type: "number", step: "0.5", min: "0" },
          { key: "notice_pay_days", label: "Notice pay - the company waived notice (days)", type: "number", step: "0.5", min: "0" },
          { key: "notice_shortfall_days", label: "Notice recovery - the employee is short of notice (days)", type: "number", step: "0.5", min: "0" },
        ]
      : [
          { key: "pay_month", label: "Month", type: "month", required: true },
          ...(v.kind === "OFF_CYCLE" ? [{ key: "title", label: "Name", required: true, max: 120, hint: "e.g. Diwali bonus" }] : []),
        ]),
    { key: "pay_date", label: "Pay date", type: "date" },
  ];
  return (
    <FormDrawer
      show={show}
      title="New payroll run"
      initial={{ kind: "REGULAR", pay_month: defaultPayMonth() }}
      fields={fields}
      onHide={onHide}
      onSave={(clean) => {
        const { kind, ...rest } = clean;
        return kind === "FNF" ? hrmsService.createFnfRun(rest) : hrmsService.createPayrollRun({ ...rest, run_type: kind });
      }}
      onDone={onDone}
    />
  );
}

function RunsTab() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listPayrollRuns(), []);
  const [creating, setCreating] = useState(false);
  const { access } = useHr();
  const canManage = hasHrPermission(access, HR_PERMISSIONS.PAYROLL_ADMIN);
  const rows = data || [];
  return (
    <>
      {canManage ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setCreating(true)}>New payroll run</PrimaryButton>
        </div>
      ) : null}
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No payroll runs yet" text="Set up pay components and each person's salary, lock attendance for the month, then start a run." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 680 }}>
              <thead>
                <tr>
                  {["Run", "Type", "Period", "Pay date", "Status"].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>
                      <Link to={`/hr/payroll/runs/${r.id}`} style={{ fontWeight: 600 }}>
                        {r.title}
                      </Link>
                    </td>
                    <td style={tdStyle}>{RUN_TYPE_LABEL[r.run_type] || r.run_type}</td>
                    <td style={tdStyle}>{formatRange(r.period_start, r.period_end)}</td>
                    <td style={tdStyle}>{r.pay_date ? formatDate(r.pay_date) : "—"}</td>
                    <td style={tdStyle}>
                      <span style={runStatusBadge(r.status)}>{runStatusLabel(r.status)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <NewRunDrawer show={creating} onHide={() => setCreating(false)} onDone={reload} />
    </>
  );
}

// Payroll: the runs, one-time items, reports and setup. Payroll administrators do the work; finance approvers only review and approve.
export default function Payroll() {
  const { access } = useHr();
  const canManage = hasHrPermission(access, HR_PERMISSIONS.PAYROLL_ADMIN);
  const [tab, setTab] = useState("runs");
  const tabs = [{ key: "runs", label: "Runs" }, ...(canManage ? [{ key: "inputs", label: "One-time items" }] : []), { key: "reports", label: "Reports" }, ...(canManage ? [{ key: "setup", label: "Setup" }] : [])];
  return (
    <div style={pageStyle}>
      <PageHeader title="Payroll" subtitle="Monthly runs, settlements, payslips and setup" />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "runs" ? <RunsTab /> : null}
      {tab === "inputs" ? <PayrollInputsTab /> : null}
      {tab === "reports" ? <PayrollReportsTab /> : null}
      {tab === "setup" ? <PayrollSetupTab /> : null}
    </div>
  );
}
